// Абонамент на наемодател/пропърти мениджър: състояние, планове, абониране.
// - Уеб: плащане с карта (Stripe), промо кодове, планове от бекенда.
// - iOS: Apple In-App Purchase (StoreKit) — по един автоматично подновяващ се
//   продукт за всеки брой имоти (com.livupp.landlord.m{N}), в една група.
//   Никакви цени от бекенда, карти или външни линкове за покупка.
// - Android: само състоянието на достъпа, без покупка.

import React, { useCallback, useEffect, useState } from 'react';
import { Linking, Platform, Switch, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { api, money } from '../../api';
import { confirmAction, showError, showSuccess } from '../../toast';
import { Badge, Button, Card, COLORS, Input, Label, Muted, Row, Screen, Title } from '../../ui';
import { LegalLinks } from '../../legal';
import * as iap from '../../iap';
import { getLocale, t } from '../../i18n';

// „iOS“ за този екран = има StoreKit в бинарния файл (1.0.1+). На стар 1.0.0 се държи както преди.
const IS_IOS = Platform.OS === 'ios' && iap.IAP_SUPPORTED;
// Страницата на Apple за управление на абонаментите (собствен екран на Apple)
const APPLE_MANAGE_URL = 'itms-apps://apps.apple.com/account/subscriptions';

type AppleCatalog = { bundleId: string; maxApartments: number; products: { productId: string; apartments: number; priceCents: number }[] };

export default function SubscriptionScreen() {
  const [data, setData] = useState<any>(null);
  const [plans, setPlans] = useState<any[]>([]);
  const [promoCode, setPromoCode] = useState('');
  const [cardInfo, setCardInfo] = useState<any>(null); // { cardEnabled, card }
  const [cardBusy, setCardBusy] = useState(false);
  const [promoInfo, setPromoInfo] = useState<any>(null); // { valid, percentOff, amountOffCents }
  // iOS: списък SKU-та от бекенда + локализираните цени от StoreKit
  const [appleCatalog, setAppleCatalog] = useState<AppleCatalog | null>(null);
  const [storeProducts, setStoreProducts] = useState<iap.IapProduct[]>([]);
  const [storeLoaded, setStoreLoaded] = useState(false);
  const [iapBusy, setIapBusy] = useState(false);

  // Проверка на промо кода на живо (с малко изчакване след писане)
  useEffect(() => {
    const code = promoCode.trim().toUpperCase();
    if (!code) {
      setPromoInfo(null);
      return;
    }
    const timer = setTimeout(() => {
      api(`/billing/promo/${encodeURIComponent(code)}`)
        .then(setPromoInfo)
        .catch(() => setPromoInfo(null));
    }, 400);
    return () => clearTimeout(timer);
  }, [promoCode]);

  // Цената след отстъпката от кода (за първия месец)
  const discounted = (cents: number) => {
    if (!promoInfo?.valid) return cents;
    if (promoInfo.percentOff != null) return Math.round((cents * (100 - promoInfo.percentOff)) / 100);
    if (promoInfo.amountOffCents) return Math.max(0, cents - promoInfo.amountOffCents);
    return cents;
  };

  const load = useCallback(() => {
    api('/billing/subscription').then(setData).catch((e) => showError(e.message));
    if (IS_IOS) {
      api('/billing/apple/products').then(setAppleCatalog).catch((e) => showError(e.message));
    } else {
      api('/billing/plans').then((r) => setPlans(r.plans)).catch(() => {});
      api('/billing/card').then(setCardInfo).catch(() => {});
    }
  }, []);
  useFocusEffect(load);

  // iOS: връзка със StoreKit за живота на екрана
  useEffect(() => {
    if (!IS_IOS) return;
    iap.connect().catch(() => {});
    return () => {
      iap.disconnect();
    };
  }, []);

  // iOS: щом знаем SKU-тата, теглим продуктите (и цените) от App Store
  useEffect(() => {
    if (!IS_IOS || !appleCatalog) return;
    let alive = true;
    const skus = appleCatalog.products.map((p) => p.productId);
    iap
      .loadProducts(skus)
      .then((items) => {
        if (!alive) return;
        setStoreProducts(items);
        setStoreLoaded(true);
      })
      .catch((e) => {
        if (!alive) return;
        setStoreLoaded(true);
        showError(e.message);
      });
    return () => {
      alive = false;
    };
  }, [appleCatalog]);

  // iOS: покупка или смяна на плана (StoreKit upgrade в същата група) →
  // потвърждение от сървъра → приключване на транзакцията
  const buyApple = async (sku: string) => {
    if (iapBusy) return;
    setIapBusy(true);
    try {
      const p = await iap.purchase(sku);
      await api('/billing/apple/verify', { body: { transactionId: p.transactionId } });
      await iap.finish(p);
      showSuccess(t('Абонаментът е активен! Всичко е възстановено.'));
      load();
    } catch (e: any) {
      if (!iap.isCancelled(e)) showError(e.message);
    } finally {
      setIapBusy(false);
    }
  };

  // iOS: възстановяване на покупките (задължително по правилата на App Store)
  const restoreApple = async () => {
    if (iapBusy) return;
    setIapBusy(true);
    try {
      const p = await iap.restore();
      if (!p) {
        showError(t('Не открихме активен абонамент за този Apple ID.'));
        return;
      }
      await api('/billing/apple/verify', { body: { transactionId: p.transactionId } });
      await iap.finish(p);
      showSuccess(t('Абонаментът е възстановен.'));
      load();
    } catch (e: any) {
      if (!iap.isCancelled(e)) showError(e.message);
    } finally {
      setIapBusy(false);
    }
  };

  const manageApple = () => {
    Linking.openURL(APPLE_MANAGE_URL).catch(() => showError(t('Не успяхме да отворим настройките на Apple ID.')));
  };

  // Уеб: връщане от Stripe (?session_id=…) → потвърждаваме запазената карта
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;
    window.history.replaceState({}, '', window.location.pathname);
    api('/billing/card/confirm', { body: { sessionId } })
      .then((r) => {
        showSuccess(t('Картата {brand} •••• {last4} е запазена.', { brand: r.card.brand, last4: r.card.last4 }));
        load();
      })
      .catch((e) => showError(e.message));
  }, [load]);

  const addCard = async () => {
    setCardBusy(true);
    try {
      const { url } = await api('/billing/card/setup', { body: { returnUrl: window.location.href.split('?')[0] } });
      window.location.href = url; // към сигурната страница на Stripe
    } catch (e: any) {
      showError(e.message);
      setCardBusy(false);
    }
  };

  const removeCard = async () => {
    try {
      await api('/billing/card', { method: 'DELETE' });
      showSuccess(t('Картата е премахната.'));
      load();
    } catch (e: any) {
      showError(e.message);
    }
  };

  const [subscribing, setSubscribing] = useState(false);
  const subscribe = async (planId: string) => {
    if (subscribing) return; // защита от двойно натискане
    setSubscribing(true);
    try {
      await api('/billing/subscribe', { body: { planId, ...(promoCode.trim() ? { promoCode: promoCode.trim() } : {}) } });
      showSuccess(t('Абонаментът е активен! Всичко е възстановено.'));
      load();
    } catch (e: any) {
      showError(e.message);
    } finally {
      setSubscribing(false);
    }
  };

  // Прекратяване: достъпът остава до края на платения период
  const cancelPlan = () => {
    const end = data?.subscription?.currentPeriodEnd ? new Date(data.subscription.currentPeriodEnd) : null;
    const daysLeft = end ? Math.max(0, Math.ceil((end.getTime() - Date.now()) / (24 * 60 * 60 * 1000))) : 0;
    confirmAction(
      t('Прекратяване на плана'),
      end
        ? daysLeft === 1
          ? t('Платили сте до {date} — остава още 1 ден пълен достъп. Нищо повече няма да се тегли, а след тази дата акаунтът минава в режим само за четене. Може да се върнете по всяко време.', { date: end.toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') })
          : t('Платили сте до {date} — остават още {n} дни пълен достъп. Нищо повече няма да се тегли, а след тази дата акаунтът минава в режим само за четене. Може да се върнете по всяко време.', { date: end.toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG'), n: daysLeft })
        : t('Абонаментът ще бъде прекратен. Може да се върнете по всяко време.'),
      t('Прекрати след изтичането'),
      async () => {
        try {
          await api('/billing/cancel', { body: {} });
          showSuccess(
            end
              ? daysLeft === 1
                ? t('Планът е прекратен. Достъпът остава до {date} (още 1 ден).', { date: end.toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') })
                : t('Планът е прекратен. Достъпът остава до {date} (още {n} дни).', { date: end.toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG'), n: daysLeft })
              : t('Планът е прекратен.')
          );
          load();
        } catch (e: any) {
          showError(e.message);
        }
      }
    );
  };

  if (!data) return <Screen><Muted>{t('Зареждане…')}</Muted></Screen>;

  const active = data.access?.active;
  // Без имоти няма какво да се плаща — карта и планове се показват
  // чак след първия имот (тогава тръгва и пробният период)
  const noApartments = data.access?.reason === 'NO_APARTMENTS_YET';
  // Уеб: Stripe карта, промо кодове и планове. iOS: Apple In-App Purchase.
  // Android: няма покупка или цени — само състоянието на достъпа.
  const isNative = Platform.OS !== 'web';
  // Абонамент, купен през App Store — прекратяване/подновяване се управляват от Apple
  const isApple = data.subscription?.provider === 'APPLE';
  const subActive = data.subscription?.status === 'ACTIVE';

  // iOS: продуктът за текущия брой имоти
  const apartmentCount: number = data.pricing?.apartmentCount ?? 0;
  const maxApartments = appleCatalog?.maxApartments ?? 20;
  const targetSku = appleCatalog?.products.find((p) => p.apartments === apartmentCount)?.productId ?? null;
  const targetStore = targetSku ? storeProducts.find((p) => p.productId === targetSku) ?? null : null;
  const appleLimit: number | null = data.subscription?.appleApartmentLimit ?? null;
  const needsUpgrade = IS_IOS && isApple && subActive && appleLimit != null && apartmentCount > appleLimit;

  return (
    <Screen>
      <Title>{t('Абонамент')}</Title>

      <Card style={{ borderColor: active ? COLORS.success : COLORS.danger, borderWidth: 2 }}>
        <Badge text={active ? t('Активен достъп') : t('ОГРАНИЧЕН РЕЖИМ (само четене)')} color={active ? COLORS.success : COLORS.danger} />
        <Text style={{ color: COLORS.text, marginTop: 8 }}>{data.access?.label}</Text>
        {data.access?.until && (
          <Muted>{t('До: {date}', { date: new Date(data.access.until).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') })}</Muted>
        )}
        {data.subscription?.plan && <Row left={t('Текущ план')} right={data.subscription.plan.name} />}
        {!isNative && data.pricing && (
          <>
            {data.pricing.custom ? (
              <Row
                left={t('Месечна цена')}
                right={data.pricing.totalCents === 0 ? t('Безплатно 🎉') : t('{amount} (индивидуална)', { amount: money(data.pricing.totalCents) })}
              />
            ) : (
              <>
                {data.pricing.perApartmentCents != null && (
                  <Row left={t('Цена')} right={t('{amount} на апартамент/месец', { amount: money(data.pricing.perApartmentCents) })} />
                )}
                <Row left={t('Ваши имоти')} right={t('{n} бр.', { n: data.pricing.apartmentCount })} />
                <View style={{ marginTop: 6, padding: 10, backgroundColor: COLORS.bg, borderRadius: 8 }}>
                  <Text style={{ fontWeight: '700', color: COLORS.text, fontSize: 16 }}>
                    {t('Общо: {amount}', { amount: data.pricing.totalCents === 0 ? t('безплатно') : t('{amount}/месец', { amount: money(data.pricing.totalCents) }) })}
                  </Text>
                  {data.pricing.perApartmentCents ? (
                    <Muted>
                      {t('{amount} × {n} имота. Добавите ли имот, сумата се преизчислява.', { amount: money(data.pricing.perApartmentCents), n: data.pricing.apartmentCount })}
                    </Muted>
                  ) : null}
                </View>
              </>
            )}
          </>
        )}

        {data.subscription && data.subscription.status !== 'TRIALING' && !isApple && (
          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 10 }}>
            <Switch
              value={data.subscription.autoRenew}
              onValueChange={async (value) => {
                try {
                  await api('/billing/auto-renew', { body: { enabled: value } });
                  showSuccess(
                    value
                      ? t('Автоматичното подновяване е включено — абонаментът ще се подновява сам.')
                      : t('Автоматичните плащания са спрени. Достъпът остава до края на платения период.')
                  );
                  load();
                } catch (e: any) {
                  showError(e.message);
                }
              }}
            />
            <Text style={{ marginLeft: 10, flex: 1, color: COLORS.text }}>
              {t('Автоматично подновяване (плащане всеки месец)')}
            </Text>
          </View>
        )}
        {data.subscription && !data.subscription.autoRenew && data.subscription.status === 'ACTIVE' && (
          <Muted>
            {t('Автоматичните плащания са спрени: след {date} акаунтът минава в режим само за четене.', { date: data.subscription.currentPeriodEnd ? new Date(data.subscription.currentPeriodEnd).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') : t('края на периода') })}
          </Muted>
        )}

        {data.subscription?.status === 'ACTIVE' && !isApple && (
          <Button title={t('Прекрати плана')} variant="ghost" onPress={cancelPlan} />
        )}
        {isApple && IS_IOS && (
          <>
            <Muted>{t('Абонаментът е през App Store. Подновяването и прекратяването се управляват от Apple ID.')}</Muted>
            <Button title={t('Управление на абонамента')} variant="secondary" onPress={manageApple} />
          </>
        )}
        {data.subscription?.status === 'CANCELED' &&
          data.subscription.currentPeriodEnd &&
          new Date(data.subscription.currentPeriodEnd) > new Date() && (
            <>
              <Text style={{ color: COLORS.text, fontWeight: '600' }}>
                {t('Планът е прекратен. Пълният достъп остава до {date} (още {n} дни) — нищо повече няма да се тегли.', {
                  date: new Date(data.subscription.currentPeriodEnd).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG'),
                  n: Math.max(1, Math.ceil((new Date(data.subscription.currentPeriodEnd).getTime() - Date.now()) / (24 * 60 * 60 * 1000))),
                })}
              </Text>
              {data.subscription.planId && !isNative && (
                <Button title={t('Възобнови плана (без ново плащане)')} onPress={() => subscribe(data.subscription.planId)} loading={subscribing} />
              )}
            </>
          )}

        {!active && (
          <Muted>
            {t('В ограничен режим не може да добавяте или променяте нищо, обявите ви са свалени, а наемателите ви не могат да плащат през приложението. Изберете план — всичко се възстановява веднага.')}
          </Muted>
        )}
      </Card>

      {noApartments && (
        <Card>
          <Text style={{ fontWeight: '700', color: COLORS.text, fontSize: 16 }}>{t('Първо добавете имот')}</Text>
          <Muted>
            {IS_IOS
              ? t('Абонаментът се появява тук, след като въведете първия си имот — тогава започва и безплатният пробен период. Дотогава не дължите нищо.')
              : t('Абонаментът, плановете и картата за плащане се появяват тук, след като въведете първия си имот — тогава започва и безплатният пробен период. Дотогава не дължите нищо.')}
          </Muted>
        </Card>
      )}

      {!noApartments && cardInfo?.cardEnabled && Platform.OS === 'web' && (
        <>
          <Label>{t('Карта за плащане')}</Label>
          <Card>
            {cardInfo.card ? (
              <>
                <Row left={t('Запазена карта')} right={`${cardInfo.card.brand} •••• ${cardInfo.card.last4}`} />
                <Muted>{t('Месечният абонамент се тегли автоматично от тази карта.')}</Muted>
                <View style={{ flexDirection: 'row', gap: 10, flexWrap: 'wrap' }}>
                  <Button title={t('Смени картата')} onPress={addCard} loading={cardBusy} />
                  <Button title={t('Премахни')} variant="ghost" onPress={removeCard} />
                </View>
              </>
            ) : (
              <>
                <Muted>
                  {t('Добавете карта, за да активирате абонамент. Данните ѝ се въвеждат на сигурната страница на Stripe — ние не ги виждаме и не ги съхраняваме.')}
                </Muted>
                <Button title={t('Добави карта')} onPress={addCard} loading={cardBusy} />
              </>
            )}
          </Card>
        </>
      )}

      {!noApartments && isNative && !IS_IOS && (
        <Card>
          <Muted>
            {t('Абонаментът за наемодатели се управлява извън мобилното приложение. Тук виждате състоянието на достъпа си.')}
          </Muted>
        </Card>
      )}

      {/* iOS: Apple In-App Purchase. Цена, период и условия са до бутона — изискване на App Store. */}
      {!noApartments && IS_IOS && apartmentCount > maxApartments && (
        <Card>
          <Text style={{ fontWeight: '700', color: COLORS.text, fontSize: 16 }}>{t('Над {n} имота', { n: maxApartments })}</Text>
          <Muted>{t('Плановете за повече от {n} имота се уреждат извън приложението.', { n: maxApartments })}</Muted>
        </Card>
      )}

      {!noApartments && IS_IOS && apartmentCount > 0 && apartmentCount <= maxApartments && !subActive && (
        <>
          <Label>{t('Абонамент')}</Label>
          <Card>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={{ fontSize: 17, fontWeight: '700', color: COLORS.text, flex: 1 }}>
                {apartmentCount === 1 ? t('Абонамент за 1 имот') : t('Абонамент за {n} имота', { n: apartmentCount })}
              </Text>
              {targetStore ? (
                <Text style={{ fontSize: 16, fontWeight: '700', color: COLORS.primary, textAlign: 'right' }}>
                  {targetStore.displayPrice}
                  <Text style={{ fontSize: 12, fontWeight: '400', color: COLORS.muted }}>{t('/месец')}</Text>
                </Text>
              ) : null}
            </View>
            {!appleCatalog || !storeLoaded ? (
              <Muted>{t('Зареждане на цената от App Store…')}</Muted>
            ) : !targetStore ? (
              <Muted>{t('Планът не е наличен в App Store в момента. Опитайте отново по-късно.')}</Muted>
            ) : null}
            <Muted>
              {t('Автоматично подновяващ се месечен абонамент. Може да го спрете по всяко време от настройките на Apple ID.')}
            </Muted>
            <LegalLinks prefix={t('С абонирането приемате')} />
            <Button
              title={t('Абонирай се')}
              onPress={() => targetSku && buyApple(targetSku)}
              disabled={!targetStore}
              loading={iapBusy}
            />
          </Card>
        </>
      )}

      {needsUpgrade && apartmentCount <= maxApartments && (
        <Card style={{ borderColor: COLORS.danger, borderWidth: 2 }}>
          <Text style={{ fontWeight: '700', color: COLORS.text, fontSize: 16 }}>{t('Планът е по-малък от имотите ви')}</Text>
          <Text style={{ color: COLORS.text, marginTop: 4 }}>
            {t('Имате {n} имота, а планът ви покрива {limit}. Сменете плана, за да добавяте имоти.', { n: apartmentCount, limit: appleLimit ?? 0 })}
          </Text>
          {targetStore ? (
            <Muted>
              {t('Нов план: {price}/месец за {n} имота. Автоматично подновяващ се месечен абонамент; разликата се преизчислява от Apple.', { price: targetStore.displayPrice, n: apartmentCount })}
            </Muted>
          ) : (
            <Muted>{t('Зареждане на цената от App Store…')}</Muted>
          )}
          <LegalLinks prefix={t('Със смяната приемате')} />
          <Button
            title={t('Смени плана')}
            onPress={() => targetSku && buyApple(targetSku)}
            disabled={!targetStore}
            loading={iapBusy}
          />
        </Card>
      )}

      {IS_IOS && (
        <Button title={t('Възстанови покупките')} variant="ghost" onPress={restoreApple} loading={iapBusy} />
      )}

      {!noApartments && !isNative && (
        <>
          <Label>{t('Промо код (по избор)')}</Label>
          <Input value={promoCode} onChangeText={setPromoCode} autoCapitalize="characters" placeholder="LIVUPP10" />
          {promoCode.trim() ? (
            promoInfo?.valid ? (
              <Text style={{ color: COLORS.success, fontWeight: '600' }}>
                {t('✓ Кодът е приет — {discount}. Цените долу са преизчислени.', {
                  discount:
                    promoInfo.percentOff != null
                      ? t('−{n}% за първия месец', { n: promoInfo.percentOff })
                      : t('−{amount} за първия месец', { amount: money(promoInfo.amountOffCents ?? 0) }),
                })}
              </Text>
            ) : (
              <Muted>{t('Този код не е валиден или е изтекъл.')}</Muted>
            )
          ) : (
            <Muted>{t('Ако имате код за отстъпка — въведете го тук и после изберете план. Прилага се веднага.')}</Muted>
          )}
        </>
      )}

      {!noApartments && !isNative && <Label>{t('Планове')}</Label>}
      {!noApartments && !isNative && plans.length === 0 && (
        <Card>
          <Muted>{t('Още няма конфигурирани планове.')}</Muted>
        </Card>
      )}
      {!noApartments && !isNative && plans.map((p) => (
        <Card key={p.id}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontSize: 17, fontWeight: '700', color: COLORS.text, flex: 1 }}>{p.name}</Text>
            <Text style={{ fontSize: 16, fontWeight: '700', color: COLORS.primary, textAlign: 'right' }}>
              {money(p.perApartmentCents)}{'\n'}
              <Text style={{ fontSize: 12, fontWeight: '400', color: COLORS.muted }}>{t('на апартамент/мес.')}</Text>
            </Text>
          </View>
          {p.description ? <Muted>{p.description}</Muted> : null}
          {data.pricing?.apartmentCount != null &&
            (promoInfo?.valid ? (
              <Text style={{ color: COLORS.text }}>
                {t('При вашите {n} имота:', { n: data.pricing.apartmentCount })}{' '}
                <Text style={{ textDecorationLine: 'line-through', color: COLORS.muted }}>
                  {money(p.perApartmentCents * data.pricing.apartmentCount)}
                </Text>{' '}
                <Text style={{ fontWeight: '700', color: COLORS.success }}>
                  {money(discounted(p.perApartmentCents * data.pricing.apartmentCount))}
                </Text>{' '}
                {t('за първия месец, после {amount}/месец', { amount: money(p.perApartmentCents * data.pricing.apartmentCount) })}
              </Text>
            ) : (
              <Muted>
                {t('При вашите {n} имота: {amount}/месец', { n: data.pricing.apartmentCount, amount: money(p.perApartmentCents * data.pricing.apartmentCount) })}
              </Muted>
            ))}
          {p.apartmentLimit ? <Muted>{t('До {n} апартамента', { n: p.apartmentLimit })}</Muted> : <Muted>{t('Без лимит на апартаментите')}</Muted>}
          {/* На телефона при включен Stripe няма бутон за покупка (App Store) */}
          {data.subscription?.planId === p.id &&
          data.subscription?.status === 'ACTIVE' &&
          data.subscription?.currentPeriodEnd &&
          new Date(data.subscription.currentPeriodEnd) > new Date() ? (
            <Text style={{ color: COLORS.success, fontWeight: '700' }}>
              {t('✓ Текущ план — платен до {date}', { date: new Date(data.subscription.currentPeriodEnd).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') })}
            </Text>
          ) : (
            <Button title={t('Избери този план')} onPress={() => subscribe(p.id)} loading={subscribing} />
          )}
        </Card>
      ))}

    </Screen>
  );
}
